/**
 * Dispatches the index route under /apps/:appSlug.
 *
 * - trading-agent / llm-chat / smart-news keep using nested sub-paths
 *   (dashboard, chat, news) so we redirect to the first sub-path.
 * - surftime / localtea mount their sub-trees DIRECTLY under /apps/:appSlug
 *   (no redundant segment), so the index renders the app's own <Routes>
 *   which handles its internal index → Home.
 * - everything else falls back to the generic placeholder.
 */
import { lazy, Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import AppPlaceholder from '@/pages/AppPlaceholder';
import { RouteFallback } from '@/components/common';

// Lazy per T062/FR-032 — this index route is reachable from App.tsx's
// eager import graph (via AppSlugIndex), so a static import here of any of
// these three app entries would pull recharts/maplibre-gl straight back
// into the entry bundle and defeat App.tsx's own lazy conversion.
const SurfTimeApp = lazy(() =>
  import('@/apps/surftime').then((m) => ({ default: m.SurfTimeApp })),
);
const LocalTeaApp = lazy(() =>
  import('@/apps/localtea').then((m) => ({ default: m.LocalTeaApp })),
);
const BitzBuggyApp = lazy(() =>
  import('@/apps/bitzbuggy').then((m) => ({ default: m.BitzBuggyApp })),
);

export default function AppSlugIndex() {
  const { appSlug } = useParams<{ appSlug: string }>();
  if (appSlug === 'trading-agent') {
    return <Navigate to="dashboard" replace />;
  }
  if (appSlug === 'llm-chat') {
    return <Navigate to="chat" replace />;
  }
  if (appSlug === 'smart-news') {
    return <Navigate to="news" replace />;
  }
  if (appSlug === 'surftime') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <SurfTimeApp />
      </Suspense>
    );
  }
  if (appSlug === 'localtea') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <LocalTeaApp />
      </Suspense>
    );
  }
  if (appSlug === 'bitzbuggy') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <BitzBuggyApp />
      </Suspense>
    );
  }
  return <AppPlaceholder />;
}
